import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { BookOpen, Users, Heart, Brain } from 'lucide-react';

const SectionContainer = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: var(--color-bg-medium);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('/path-to-texture.png');
    opacity: 0.03;
    pointer-events: none;
  }
`;

const SectionContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
`;

const SectionHeading = styled(motion.div)`
  text-align: center;
  margin-bottom: var(--spacing-lg);
`;

const HeadingLabel = styled(motion.div)`
  color: var(--color-accent-primary);
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: var(--spacing-sm);
`;

const SectionTitle = styled(motion.h2)`
  margin-bottom: var(--spacing-md);
`;

const SectionDescription = styled(motion.p)`
  max-width: 700px;
  margin: 0 auto;
  color: var(--color-text-secondary);
  font-size: 1.25rem;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: var(--spacing-md);
  margin-top: var(--spacing-lg);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.05) 0%, 
    rgba(255, 255, 255, 0.02) 100%);
  border-radius: var(--radius-lg);
  padding: var(--spacing-md);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
    
    .service-icon {
      transform: translateY(-5px);
      background-color: var(--color-accent-primary);
      color: var(--color-bg-dark);
    }
    
    .service-accent {
      opacity: 0.2;
    }
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: var(--radius-md);
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-md);
  color: var(--color-accent-primary);
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
`;

const ServiceTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
  font-size: 1.5rem;
  position: relative;
  z-index: 1;
`;

const ServiceDescription = styled.p`
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const ServicePoints = styled.ul`
  list-style: none;
  margin-top: var(--spacing-sm);
  position: relative;
  z-index: 1;
`;

const ServicePoint = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-xs);
  
  svg {
    flex-shrink: 0;
    color: var(--color-accent-primary);
    margin-right: 0.5rem;
  }
`;

const ServiceAccent = styled.div`
  position: absolute;
  top: -50px;
  right: -50px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  background: radial-gradient(circle, var(--color-accent-primary) 0%, transparent 70%);
  opacity: 0.05;
  transition: opacity 0.3s ease;
`;

const LearnMoreButton = styled(motion.button)`
  background-color: transparent;
  color: var(--color-accent-primary);
  border: 1px solid var(--color-accent-primary);
  border-radius: var(--radius-full);
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  position: relative;
  z-index: 1;
  overflow: hidden;
  
  svg {
    margin-left: 0.5rem;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    svg {
      transform: translateX(3px);
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 0;
    height: 100%;
    background-color: var(--color-accent-primary);
    transition: width 0.3s ease;
    z-index: -1;
  }
  
  &:hover {
    color: var(--color-bg-dark);
    
    &::before {
      width: 100%;
    }
  }
`;

const servicesData = [
  {
    id: 1,
    icon: <BookOpen size={24} />,
    title: "Comprehensive Psychological Assessment",
    description: "Detailed evaluations that go beyond diagnosis to provide actionable recommendations for classroom and home.",
    points: [
      "Psychoeducational evaluations for learning disabilities",
      "Social-emotional and behavioral assessments",
      "Functional Behavior Analysis (FBA)",
      "Autism-specific evaluations"
    ]
  },
  {
    id: 2,
    icon: <Users size={24} />,
    title: "Educational Consultation",
    description: "Expert guidance for schools implementing evidence-based practices and inclusive educational environments.",
    points: [
      "MTSS/RTI implementation and refinement",
      "Behavior intervention plan development",
      "Teacher coaching and support",
      "Crisis prevention systems"
    ]
  },
  {
    id: 3,
    icon: <Brain size={24} />,
    title: "Professional Development",
    description: "Engaging workshops designed to enhance educator skills in supporting diverse learners and fostering positive school climate.",
    points: [
      "Crisis prevention training",
      "Social-emotional learning implementation",
      "Supporting students with autism",
      "Executive functioning strategies"
    ]
  },
  {
    id: 4,
    icon: <Heart size={24} />,
    title: "Parent Advocacy & Support",
    description: "Compassionate guidance for families navigating the educational system, particularly for children with special needs.",
    points: [
      "IEP and 504 process navigation",
      "Educational rights education",
      "School-home communication support",
      "Home strategy implementation"
    ]
  }
];

const AnimatedServices = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, amount: 0.2 });
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  
  return (
    <SectionContainer id="services">
      <SectionContent>
        <SectionHeading>
          <HeadingLabel
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}
          >
            How I Can Help
          </HeadingLabel>
          
          <SectionTitle
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            My Services
          </SectionTitle>
          
          <SectionDescription
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            As an experienced school psychologist, I offer a range of specialized services designed to support students, educators, and families in creating effective learning environments.
          </SectionDescription>
        </SectionHeading>
        
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <ServicesGrid>
            {servicesData.map((service, index) => (
              <ServiceCard
                key={service.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <ServiceIcon className="service-icon">
                  {service.icon}
                </ServiceIcon>
                
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
                
                <ServicePoints>
                  {service.points.map((point, i) => (
                    <ServicePoint key={i}>
                      <motion.svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        initial={{ opacity: 0, x: -10 }}
                        animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
                        transition={{ duration: 0.3, delay: 0.4 + i * 0.1 }}
                      >
                        <path
                          d="M5 12L9 16L19 6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </motion.svg>
                      {point}
                    </ServicePoint>
                  ))}
                </ServicePoints>
                
                <LearnMoreButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Learn more
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5 12H19M19 12L12 5M19 12L12 19"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </LearnMoreButton>
                
                <ServiceAccent className="service-accent" />
              </ServiceCard>
            ))}
          </ServicesGrid>
        </motion.div>
      </SectionContent>
    </SectionContainer>
  );
};

export default AnimatedServices;